// AiStudyHomeCard — exibe os últimos cadernos/redações na Home do aluno.
// Se o usuário não tem acesso à feature (403), o card não renderiza nada
// (não vira ruído visual pra quem ainda não foi liberado).

import { useEffect, useState } from "preact/hooks";
import {
  ChevronRight,
  Database,
  FileText,
  Loader2,
  Sparkles
} from "lucide-react";
import { api, ApiError, type AiStudySessionRow, type HomeBank } from "./api";
import { kindTheme } from "./AiStudyView";
import { data as homeData, setActiveNav } from "./App";
import { openBank } from "./BankDetailView";

const MAX_ITEMS = 3;

export function AiStudyHomeCard({ onOpen }: { onOpen: () => void }) {
  const [phase, setPhase] = useState<"loading" | "ready" | "no_access" | "error">("loading");
  const [sessions, setSessions] = useState<AiStudySessionRow[]>([]);

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        const r = await api.listAiStudy();
        if (canceled) return;
        const active = r.sessions
          .filter((s) => !s.archivedAt)
          .slice(0, MAX_ITEMS);
        setSessions(active);
        setPhase("ready");
      } catch (e) {
        if (canceled) return;
        if (e instanceof ApiError && e.status === 403) {
          setPhase("no_access");
          return;
        }
        setPhase("error");
      }
    })();
    return () => {
      canceled = true;
    };
  }, []);

  // Empty state — antes esse bloco retornava null em no_access/error, o que
  // deixava um buraco visível no grid de blocos da home. Agora sempre renderiza
  // o frame com um conteúdo informativo, alinhando com os outros HomeCards.
  return (
    <section class="rounded-lg border border-border bg-gradient-to-br from-sky-500/5 via-background to-violet-500/5 p-5">
      <header class="mb-3 flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <span class="grid size-8 place-items-center rounded-md bg-gradient-to-br from-sky-500/20 to-violet-500/20 text-sky-700 dark:text-sky-300">
            <Sparkles className="size-4" />
          </span>
          <div>
            <h2 class="text-base font-bold tracking-tight">Estudando com IA</h2>
            <p class="text-[11px] text-muted-foreground">
              <span class="text-sky-600 dark:text-sky-300">Cadernos</span> e{" "}
              <span class="text-violet-600 dark:text-violet-300">redações</span> com IA tutora.
            </p>
          </div>
        </div>
        {phase !== "no_access" && phase !== "error" && (
          <button
            type="button"
            onClick={onOpen}
            class="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            Abrir tudo <ChevronRight className="size-3" />
          </button>
        )}
      </header>

      {phase === "no_access" && (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-3 py-3 text-center text-xs text-muted-foreground">
          Esta funcionalidade ainda não está habilitada para você. Entre em contato com a equipe se quiser experimentar.
        </div>
      )}

      {phase === "error" && (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-3 py-3 text-center text-xs text-muted-foreground">
          Não consegui carregar os estudos agora. Tente recarregar a página em alguns instantes.
        </div>
      )}

      {phase === "loading" && (
        <p class="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="size-3 animate-spin" /> Carregando…
        </p>
      )}

      {phase === "ready" && sessions.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-3 py-4 text-center text-xs text-muted-foreground">
          Você ainda não tem nenhum estudo. Comece pelo menu lateral.
        </div>
      )}

      {/* Grade unificada: Cadernos + Redações + Último Banco compartilham
          o mesmo grid `sm:grid-cols-3` pra ficarem TODOS na mesma linha
          (no desktop). Caderno/Redação ocupam slots na ordem que vieram
          do servidor; banco entra como último slot quando existe. */}
      {phase === "ready" && (sessions.length > 0 || hasUltimoBanco()) && (
        <ul class="grid gap-2 sm:grid-cols-3">
          {sessions.map((s) => {
            const isEssay = s.kind === "essay";
            const theme = kindTheme(s.kind);
            return (
              <li
                key={s.id}
                onClick={onOpen}
                class={`group cursor-pointer rounded-md border bg-card/60 px-3 py-2 transition-colors ${theme.border} ${theme.hover}`}
              >
                <div class="mb-1 flex items-start gap-1.5">
                  {isEssay ? (
                    <FileText className={`mt-0.5 size-3.5 shrink-0 ${theme.icon}`} />
                  ) : (
                    <Sparkles className={`mt-0.5 size-3.5 shrink-0 ${theme.icon}`} />
                  )}
                  <h3 class="line-clamp-1 flex-1 text-xs font-bold">{s.title}</h3>
                </div>
                <p class="line-clamp-2 text-[10px] text-muted-foreground">{s.theme}</p>
                <div class="mt-1.5 flex flex-wrap items-center gap-1 text-[9px] uppercase tracking-wider">
                  <span class={`rounded-md px-1.5 py-0.5 font-bold ${theme.badge}`}>
                    {theme.label}
                  </span>
                  {isEssay && typeof s.essayScore === "number" && (
                    <span class="rounded-md bg-emerald-500/15 px-1.5 py-0.5 font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                      {s.essayScore.toFixed(2).replace(".", ",")}
                    </span>
                  )}
                  <span class="ml-auto text-muted-foreground">
                    {new Date(s.updatedAt).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </li>
            );
          })}
          <UltimoBancoLi />
        </ul>
      )}
    </section>
  );
}

function hasUltimoBanco(): boolean {
  const home = homeData.value;
  if (!home) return false;
  return home.banks.some((b) => b.lastActivityAt != null);
}

// Renderiza UM <li> com o último banco trabalhado, no MESMO formato
// visual dos cards de Caderno/Redação (border/padding equivalente)
// pra alinhar no grid. Retorna null se não há banco com atividade.
function UltimoBancoLi() {
  const home = homeData.value;
  if (!home) return null;
  const bank = home.banks.find((b) => b.lastActivityAt != null);
  if (!bank) return null;

  const pct = bank.estudo.accuracyPct;
  const respondidasPct =
    bank.questionCount > 0
      ? Math.round((bank.estudo.respondidas / bank.questionCount) * 100)
      : 0;
  const ms = Date.now() - Date.parse(bank.lastActivityAt!);
  const min = Math.round(ms / 60_000);
  let quando: string;
  if (min < 1) quando = "agora";
  else if (min < 60) quando = `há ${min} min`;
  else if (min < 60 * 24) quando = `há ${Math.round(min / 60)} h`;
  else if (min < 60 * 48) quando = "ontem";
  else quando = `há ${Math.round(min / 60 / 24)} dias`;

  return (
    <li
      onClick={() => {
        setActiveNav("bancos");
        openBank(bank.id);
      }}
      class="group cursor-pointer rounded-md border border-primary/30 bg-card/60 px-3 py-2 transition-colors hover:border-primary/60 hover:bg-card"
    >
      <div class="mb-1 flex items-start gap-1.5">
        <Database className="mt-0.5 size-3.5 shrink-0 text-primary" />
        <h3 class="line-clamp-1 flex-1 text-xs font-bold">{bank.name}</h3>
      </div>
      <div class="mt-1 h-1 overflow-hidden rounded-full bg-muted">
        <div
          class="h-full rounded-full bg-primary transition-all"
          style={{ width: `${respondidasPct}%` }}
        />
      </div>
      <div class="mt-1.5 flex flex-wrap items-center gap-1 text-[9px] uppercase tracking-wider">
        <span class="rounded-md bg-primary/15 px-1.5 py-0.5 font-bold text-primary">
          Banco
        </span>
        <span class="rounded-md bg-card px-1.5 py-0.5 font-bold tabular-nums text-muted-foreground">
          {bank.estudo.respondidas}/{bank.questionCount}
        </span>
        {pct != null && (
          <span class="rounded-md bg-emerald-500/15 px-1.5 py-0.5 font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {pct}%
          </span>
        )}
        <span class="ml-auto text-muted-foreground">{quando}</span>
      </div>
    </li>
  );
}
